import React from "react";
import {
  Box,
  Heading,
  Text,
  Grid,
  VStack,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Badge,
  Progress,
  useColorModeValue,
} from "@chakra-ui/react";
import { PracticeExamAttemptScore, QuestionSetPracticeAttemptScore } from "../../../Platform/Common/types";
import { fmtLocal } from "../../../utils/dates";
import { getResultPresentation } from "../../../utils/resultPresentation";

interface B2CScoreViewerProps {
  scoreData: PracticeExamAttemptScore;
}

const B2CScoreViewer: React.FC<B2CScoreViewerProps> = ({ scoreData }) => {
  const questionPaneBg = useColorModeValue("gray.50", "gray.700");
  const questionPaneBg1 = useColorModeValue("white", "gray.600");

  const overallResult = getResultPresentation(scoreData.has_passed, scoreData.overall_percentage);

  return (
    <Box>
      {/* Exam Summary Card */}
      <Box
        borderWidth="1px"
        borderRadius="2xl"
        p={6}
        mb={6}
        boxShadow="md"
        bg={questionPaneBg}
      >
        <Heading size="lg" mb={2}>
          {scoreData.title}
        </Heading>

        <Text mb={1}>Start: {fmtLocal(scoreData.student_start_time)}</Text>
        <Text mb={1}>
          End:&nbsp;
          {scoreData.student_end_time ? fmtLocal(scoreData.student_end_time) : "N/A"}
        </Text>

        <Grid templateColumns={["1fr", "repeat(3, 1fr)"]} gap={4} mt={4}>
          <Stat>
            <StatLabel>Score</StatLabel>
            <StatNumber>{scoreData.total_score_obtained.toFixed(2)} / {scoreData.max_exam_score}</StatNumber>
            <StatHelpText>Obtained vs Max</StatHelpText>
          </Stat>

          <Stat>
            <StatLabel>Percentage</StatLabel>
            <StatNumber>{scoreData.overall_percentage.toFixed(2)}%</StatNumber>
            <StatHelpText>Target: {scoreData.pass_percentage}%</StatHelpText>
          </Stat>

          <Stat>
            <StatLabel>Result</StatLabel>
            <StatNumber fontSize="lg">
              <Badge colorScheme={overallResult.colorScheme} fontSize="sm" px={2} py={0.5}>
                {overallResult.label}
              </Badge>
            </StatNumber>
          </Stat>
        </Grid>

        <Box mt={4}>
          <Progress
            value={scoreData.overall_percentage}
            size="sm"
            borderRadius="full"
            colorScheme={overallResult.colorScheme}
          />
          <Text mt={2} fontSize="sm" color="gray.600">
            {overallResult.message}
          </Text>
        </Box>

        {scoreData.remarks && (
          <Text mt={4} fontStyle="italic" color="gray.500">
            Remarks: {scoreData.remarks}
          </Text>
        )}
      </Box>

      {/* Question Set Score Grid */}
      <VStack align="stretch" spacing={4}>
        {scoreData.attempted_question_set_score.map((qs: QuestionSetPracticeAttemptScore) => {
          const qsResult = getResultPresentation(qs.has_passed, qs.percentage_obtained);

          return (
            <Box
              key={qs.practice_attempted_question_set_id}
              borderWidth="1px"
              borderRadius="xl"
              p={4}
              boxShadow="sm"
              bg={questionPaneBg1}
            >
              <Heading size="md" mb={2}>
                {qs.sequence}. {qs.title}
              </Heading>
              <Text>Start: {fmtLocal(qs.student_start_time)}</Text>
              <Text mb={1}>
                End:&nbsp;
                {qs.student_end_time ? fmtLocal(qs.student_end_time) : "N/A"}
              </Text>
              <Text>
                Score: {qs.total_score_obtained.toFixed(2)} / {qs.max_qset_score} (
                {qs.percentage_obtained.toFixed(2)}%)
              </Text>
              <Text>Target: {qs.pass_percentage}%</Text>
              <Text>
                Result:{" "}
                <Badge colorScheme={qsResult.colorScheme}>
                  {qsResult.label}
                </Badge>
              </Text>
              {qs.remarks && (
                <Text mt={2} fontStyle="italic" color="gray.600">
                  Remarks: {qs.remarks}
                </Text>
              )}
            </Box>
          );
        })}
      </VStack>
    </Box>
  );
};

export default B2CScoreViewer;